import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit } from 'lucide-react';
import { categoriesAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '' });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, formData);
        toast.success('Category updated successfully!');
      } else {
        await categoriesAPI.create(formData);
        toast.success('Category created successfully!');
      }
      loadCategories();
      handleCloseDialog();
    } catch (error) {
      toast.error(editingCategory ? 'Failed to update category' : 'Failed to create category');
      console.error('Error saving category:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await categoriesAPI.delete(id);
      toast.success('Category deleted successfully!');
      loadCategories();
    } catch (error) {
      toast.error('Failed to delete category');
      console.error('Error deleting category:', error);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setFormData({ name: '', slug: '', description: '' });
  };

  const handleNameChange = (name) => {
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setFormData({ ...formData, name, slug });
  };

  return (
    <div data-testid="admin-categories-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold font-['Syne']" data-testid="categories-title">Categories</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black hover:bg-primary/90" 
                data-testid="add-category-button"
                onClick={() => {
                  setEditingCategory(null);
                  setFormData({ name: '', slug: '', description: '' });
                }}
              >
                <Plus size={18} className="mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="category-dialog">
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="category-form">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Category Name</label>
                  <Input
                    placeholder="e.g., Web Development"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required
                    data-testid="category-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Slug (URL-friendly)</label>
                  <Input
                    placeholder="e.g., web-development"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    data-testid="category-slug-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Description (Optional)</label>
                  <Textarea
                    placeholder="Brief description of this category"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    data-testid="category-description-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={handleCloseDialog}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="w-full bg-primary text-black hover:bg-primary/90" 
                    data-testid="category-save-button"
                  >
                    {editingCategory ? 'Update Category' : 'Create Category'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-20" data-testid="categories-loading">
            <p className="text-muted-foreground">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-20" data-testid="no-categories">
            <p className="text-muted-foreground">No categories yet. Create your first one!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-list">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-card border border-white/10 p-6 rounded-lg"
                data-testid={`category-item-${category.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold font-['Syne'] mb-1" data-testid={`category-name-${category.id}`}>
                      {category.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">{category.slug}</p>
                    {category.description && (
                      <p className="text-sm text-muted-foreground/80">{category.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(category)}
                    className="flex-1"
                    data-testid={`edit-category-${category.id}`}
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(category.id)}
                    className="text-red-500 hover:text-red-600"
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminCategories;
